import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faChevronRight,
  faClock,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import CountdownTimer from "./CountdownTimer";

const UpcomingEventsCard = ({
  events = [],
  isLoading,
  onEventClick = () => {},
  onViewAllClick = () => {},
  title = "Upcoming Events",
  subtitle = "Next UFC & MMA events",
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded-xl"></div>
          <div className="h-20 bg-gray-200 rounded-xl"></div>
          <div className="h-20 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-dark-grey to-gray-700 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-deep-red w-5 h-5"
            />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <p className="text-white/80 text-sm">{subtitle}</p>
          </div>
        </div>
        {onViewAllClick && (
          <button
            onClick={onViewAllClick}
            className="text-gold hover:text-white transition-colors text-sm font-bold flex items-center gap-1"
          >
            View All
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
          </button>
        )}
      </div>

      {events && events.length > 0 ? (
        <div className="p-4 space-y-3">
          {events.slice(0, 4).map((event) => (
            <div
              key={event.id}
              className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => onEventClick(event.id, event.name)}
            >
              {/* Countdown Timer Banner */}
              {event.showCountdown && (
                <div className="bg-deep-red text-white px-4 py-2 text-xs font-bold flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                  <CountdownTimer
                    targetDate={event.dateTime}
                    className="bg-transparent text-white p-0 animate-pulse"
                    showIcon={false}
                  />
                </div>
              )}

              {/* Event Started Banner */}
              {event.hasStarted && !event.showCountdown && (
                <div className="bg-deep-red text-white px-4 py-2 text-xs font-bold text-center">
                  <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                  Event Started
                </div>
              )}

              <div className="p-4 flex items-center gap-4">
                {/* Date Card */}
                <div className="flex-shrink-0 w-12 h-14 rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                  <div className="bg-deep-red text-white text-center py-1 text-xs font-bold uppercase">
                    {event.monthShort}
                  </div>
                  <div className="bg-white text-center flex items-center justify-center h-10 font-bold text-lg text-dark-grey">
                    {event.day}
                  </div>
                </div>

                {/* Event Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-dark-grey text-base leading-tight line-clamp-2">
                      {event.name}
                    </h3>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-medium-grey">
                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-deep-red w-3 h-3"
                      />
                      <span className="font-medium">{event.formattedTime}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-1">
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="text-deep-red w-3 h-3"
                        />
                        <span className="font-medium truncate">
                          {event.location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 text-center flex flex-col justify-center items-center min-h-[200px]">
          <div className="bg-gray-100 rounded-full p-4 mb-4 w-16 h-16 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-deep-red w-6 h-6"
            />
          </div>
          <h3 className="text-dark-grey font-bold text-lg mb-2">
            No Upcoming Events
          </h3>
          <p className="text-medium-grey text-sm max-w-[250px] mb-4">
            Check back soon for new events or explore other sections.
          </p>
          {onViewAllClick && (
            <button
              onClick={onViewAllClick}
              className="bg-gray-200 text-dark-grey px-4 py-3 rounded-xl text-sm font-bold hover:bg-gray-300 transition-colors min-h-[44px] flex items-center gap-2"
            >
              Browse All Events
              <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UpcomingEventsCard;
